import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, Clock, BarChart3, TrendingUp, ArrowLeft, RefreshCw, Lock, Eye, EyeOff, Settings, Mail, User, KeyRound, Save, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Settings state
  const [activeTab, setActiveTab] = useState("dashboard");
  const [adminSettings, setAdminSettings] = useState({ username: "", recovery_email: "" });
  const [newUsername, setNewUsername] = useState("");
  const [usernamePassword, setUsernamePassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('admin_authenticated');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: username,
        password: password
      });
      
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_authenticated', 'true');
      }
    } catch (error) {
      setLoginError("Invalid username or password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`);
      toast.success("Password sent to recovery email");
    } catch (error) {
      toast.error("Failed to send recovery email");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    setActiveTab("dashboard");
  };

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/analytics/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAuthenticated]);

  const fetchAdminSettings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setAdminSettings(response.data);
      setRecoveryEmail(response.data.recovery_email || "");
    } catch (error) {
      console.error("Failed to fetch admin settings:", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchStats();
    fetchAdminSettings();
  }, [fetchStats, fetchAdminSettings]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    if (!newUsername || !usernamePassword) {
      toast.error("Please fill in all fields");
      return;
    }
    setSavingUsername(true);
    try {
      await axios.post(`${API}/admin/change-username`, {
        password: usernamePassword,
        new_username: newUsername
      });
      toast.success("Username updated successfully");
      setNewUsername("");
      setUsernamePassword("");
      fetchAdminSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update username");
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleUpdateRecoveryEmail = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setSavingEmail(true);
    try {
      await axios.post(`${API}/admin/update-recovery-email`, {
        email: recoveryEmail
      });
      toast.success("Recovery email updated");
      fetchAdminSettings();
    } catch (error) {
      toast.error("Failed to update recovery email");
    } finally {
      setSavingEmail(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-zinc-950 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-outfit text-2xl font-medium text-zinc-950 mb-2">Admin Login</h1>
            <p className="font-manrope text-zinc-500">Enter your credentials to access the dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block font-outfit text-sm text-zinc-600 mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full"
                data-testid="admin-username-input"
              />
            </div>
            
            <div>
              <label className="block font-outfit text-sm text-zinc-600 mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full pr-10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {loginError && (
              <p className="text-red-500 text-sm font-manrope" data-testid="login-error">{loginError}</p>
            )}
            
            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-zinc-950 hover:bg-zinc-800 text-white py-3 rounded-full font-outfit disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loginLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
          
          <div className="mt-6 text-center space-y-3">
            <button
              onClick={handleForgotPassword}
              disabled={forgotPasswordLoading}
              className="font-manrope text-sm text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50"
            >
              {forgotPasswordLoading ? "Sending..." : "Forgot password?"}
            </button>
            <div>
              <a 
                href="/" 
                className="font-manrope text-sm text-zinc-400 hover:text-zinc-600 transition-colors"
              >
                ← Back to website
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="py-6 px-6 border-b border-zinc-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-manrope">Back</span>
            </a>
            <h1 className="font-outfit text-xl font-medium text-zinc-950">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50"
              data-testid="refresh-stats"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="font-manrope hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-500 hover:text-red-700 transition-colors"
              data-testid="logout-button"
            >
              <Lock className="w-4 h-4" />
              <span className="font-manrope">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`py-4 font-outfit text-sm font-medium border-b-2 transition-colors ${
                activeTab === "dashboard" 
                  ? "border-zinc-950 text-zinc-950" 
                  : "border-transparent text-zinc-400 hover:text-zinc-600"
              }`}
              data-testid="tab-dashboard"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`py-4 font-outfit text-sm font-medium border-b-2 transition-colors ${
                activeTab === "settings" 
                  ? "border-zinc-950 text-zinc-950" 
                  : "border-transparent text-zinc-400 hover:text-zinc-600"
              }`}
              data-testid="tab-settings"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </div>

      <main className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          
          {activeTab === "dashboard" && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {/* Total Visitors */}
                <div className="bg-zinc-50 rounded-3xl p-6 border border-zinc-100" data-testid="stat-visitors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-zinc-200">
                      <Users className="w-6 h-6 text-zinc-600" />
                    </div>
                    <span className="font-outfit text-sm text-zinc-500 uppercase tracking-wide">Visitors</span>
                  </div>
                  <div className="font-outfit text-4xl font-bold text-zinc-950">
                    {stats?.total_visitors || 0}
                  </div>
                  <p className="font-manrope text-sm text-zinc-400 mt-1">Unique visitors</p>
                </div>

                {/* Total Sessions */}
                <div className="bg-zinc-50 rounded-3xl p-6 border border-zinc-100" data-testid="stat-sessions">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-zinc-200">
                      <BarChart3 className="w-6 h-6 text-zinc-600" />
                    </div>
                    <span className="font-outfit text-sm text-zinc-500 uppercase tracking-wide">Sessions</span>
                  </div>
                  <div className="font-outfit text-4xl font-bold text-zinc-950">
                    {stats?.total_sessions || 0}
                  </div>
                  <p className="font-manrope text-sm text-zinc-400 mt-1">Total sessions</p>
                </div>

                {/* Total Hours */}
                <div className="bg-zinc-50 rounded-3xl p-6 border border-zinc-100" data-testid="stat-hours">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-zinc-200">
                      <Clock className="w-6 h-6 text-zinc-600" />
                    </div>
                    <span className="font-outfit text-sm text-zinc-500 uppercase tracking-wide">Time Spent</span>
                  </div>
                  <div className="font-outfit text-4xl font-bold text-zinc-950">
                    {stats?.total_hours_spent || 0}
                  </div>
                  <p className="font-manrope text-sm text-zinc-400 mt-1">Hours on site</p>
                </div>

                {/* Total Comparisons */}
                <div className="bg-zinc-50 rounded-3xl p-6 border border-zinc-100" data-testid="stat-comparisons">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-zinc-200">
                      <TrendingUp className="w-6 h-6 text-zinc-600" />
                    </div>
                    <span className="font-outfit text-sm text-zinc-500 uppercase tracking-wide">Comparisons</span>
                  </div>
                  <div className="font-outfit text-4xl font-bold text-zinc-950">
                    {stats?.total_comparisons || 0}
                  </div>
                  <p className="font-manrope text-sm text-zinc-400 mt-1">Total comparisons</p>
                </div>
              </div>

              {/* Top 10 Comparisons */}
              <div className="bg-white rounded-3xl border border-zinc-100 p-8" style={{boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)'}}>
                <h2 className="font-outfit text-2xl font-medium text-zinc-950 mb-6">
                  Top 10 Comparisons
                </h2>
                
                {stats?.top_comparisons && stats.top_comparisons.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="top-comparisons-table">
                      <thead>
                        <tr className="border-b-2 border-zinc-950">
                          <th className="text-left py-3 px-4 font-outfit text-sm font-medium text-zinc-500">#</th>
                          <th className="text-left py-3 px-4 font-outfit text-sm font-medium text-zinc-950">Device 1</th>
                          <th className="text-left py-3 px-4 font-outfit text-sm font-medium text-zinc-400">vs</th>
                          <th className="text-left py-3 px-4 font-outfit text-sm font-medium text-zinc-950">Device 2</th>
                          <th className="text-right py-3 px-4 font-outfit text-sm font-medium text-zinc-950">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_comparisons.map((comparison, index) => (
                          <tr 
                            key={index} 
                            className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
                            data-testid={`comparison-row-${index}`}
                          >
                            <td className="py-4 px-4 font-manrope text-zinc-400">{index + 1}</td>
                            <td className="py-4 px-4 font-manrope text-zinc-900">{comparison.device1}</td>
                            <td className="py-4 px-4 font-manrope text-zinc-300">vs</td>
                            <td className="py-4 px-4 font-manrope text-zinc-900">{comparison.device2}</td>
                            <td className="py-4 px-4 text-right">
                              <span className="inline-flex items-center justify-center min-w-[40px] px-3 py-1 bg-zinc-100 rounded-full font-outfit font-medium text-zinc-700">
                                {comparison.count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="font-manrope text-zinc-400">No comparisons yet</p>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === "settings" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Change Username */}
              <div className="bg-white rounded-3xl border border-zinc-100 p-8" style={{boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)'}}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-zinc-600" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-medium text-zinc-950">Change Username</h3>
                    <p className="font-manrope text-sm text-zinc-400">Current: {adminSettings.username}</p>
                  </div>
                </div>
                
                <form onSubmit={handleChangeUsername} className="space-y-4">
                  <div>
                    <label className="block font-outfit text-sm text-zinc-600 mb-2">New Username</label>
                    <Input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="Enter new username"
                      data-testid="new-username-input"
                    />
                  </div>
                  <div>
                    <label className="block font-outfit text-sm text-zinc-600 mb-2">Current Password</label>
                    <Input
                      type="password"
                      value={usernamePassword}
                      onChange={(e) => setUsernamePassword(e.target.value)}
                      placeholder="Enter your password"
                      data-testid="username-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingUsername}
                    className="w-full bg-zinc-950 hover:bg-zinc-800"
                    data-testid="save-username-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingUsername ? "Saving..." : "Update Username"}
                  </Button>
                </form>
              </div>

              {/* Change Password */}
              <div className="bg-white rounded-3xl border border-zinc-100 p-8" style={{boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)'}}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-zinc-600" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-medium text-zinc-950">Change Password</h3>
                    <p className="font-manrope text-sm text-zinc-400">Update your admin password</p>
                  </div>
                </div>
                
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block font-outfit text-sm text-zinc-600 mb-2">Current Password</label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <label className="block font-outfit text-sm text-zinc-600 mb-2">New Password</label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <label className="block font-outfit text-sm text-zinc-600 mb-2">Confirm New Password</label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingPassword}
                    className="w-full bg-zinc-950 hover:bg-zinc-800"
                    data-testid="save-password-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingPassword ? "Saving..." : "Update Password"}
                  </Button>
                </form>
              </div>

              {/* Recovery Email */}
              <div className="bg-white rounded-3xl border border-zinc-100 p-8 lg:col-span-2" style={{boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)'}}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-zinc-600" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-medium text-zinc-950">Recovery Email</h3>
                    <p className="font-manrope text-sm text-zinc-400">
                      Current: {adminSettings.recovery_email || "Not set"}
                    </p>
                  </div>
                </div>
                
                <form onSubmit={handleUpdateRecoveryEmail} className="flex flex-col sm:flex-row gap-4">
                  <Input
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="Enter recovery email address"
                    className="flex-1"
                    data-testid="recovery-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={savingEmail}
                    className="bg-zinc-950 hover:bg-zinc-800 whitespace-nowrap"
                    data-testid="save-email-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingEmail ? "Saving..." : "Update Email"}
                  </Button>
                </form>
                <p className="font-manrope text-xs text-zinc-400 mt-4">
                  This email will be used to recover your password if you forget it.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
